import React, { useEffect, useState } from 'react';
import TextField from '../components/atoms/TextField';
import PasswordInput from '../components/atoms/PasswordInput';
import Label from '../components/atoms/Label';
import '../styles/SignInAndSecurity.css';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import Button from '../components/atoms/Button';
import 'react-toastify/dist/ReactToastify.css';
import { toast, ToastContainer } from 'react-toastify';
import { useSelector, useDispatch } from 'react-redux';
import { updateUserDetails } from '../services/PersonalDetailsApi';
import { logger } from '../utils/logger';
import { set } from 'lodash';
import useIsMobile from '../hooks/ismobile.js';

const SignInAndSecurity = () => {
	const dispatch = useDispatch();
	const isMobile = useIsMobile();

	// To store values from Database
	const { emailId: emailId, phoneNumber: initialContactNumber } = useSelector(
		(state) => state.userDetails
	);

	const [contactNumber, setContactNumber] = useState('');
	const [phoneError, setPhoneError] = useState('');
	const [isLoading, setIsLoading] = useState(false);

	const loadPersonalDetails = async () => {
		setContactNumber(initialContactNumber);
		formValidation.contactNumber = initialContactNumber.trim().length > 0;
	};

	useEffect(() => {
		loadPersonalDetails();
	}, []);

	const handleChange = (value) => {
		setContactNumber(value);
		const validationError = validateMobileNumber(value);
		setPhoneError(validationError);
		handleFieldValidation('contactNumber', !validationError);
	};

	const handleBlur = () => {
		const validationError = validateMobileNumber(contactNumber);
		setPhoneError(validationError);
	};

	const validateMobileNumber = (value) => {
		if (value) {
			const mobileRegex = /^\+\d{1,3}\s?\d{10}$/;
			if (!value.trim()) {
				return 'Mobile number is required.';
			} else if (!mobileRegex.test(value)) {
				return 'Enter a valid mobile number with country code (e.g., +1 1234567890).';
			}
		}
		return ''; // No error
	};

	const [formValidation, setFormValidation] = useState({
		contactNumber: true,
		password: true,
		retypePassword: true,
	});

	const isFormValid = () => {
		return (
			formValidation.contactNumber && formValidation.password && formValidation.retypePassword
		);
	};

	const handleFieldValidation = (field, isValid) => {
		setFormValidation((prev) => {
			const updated = {
				...prev,
				[field]: isValid,
			};
			logger.log(`Field ${field} validation updated:`, isValid);
			logger.log('Current form validation:', updated);
			return updated;
		});
	};

	const updateUserData = async () => {
		try {
			setIsLoading(true);
			const email = emailId;
			const updates = {
				phoneNumber: contactNumber,
			};

			if (password) {
				updates.password = password;
			}

			let response = await updateUserDetails(email, updates);
			if (response.success) {
				toast.success('Details updated successfully!');
			} else {
				toast.error('Error updating user details');
			}
		} catch (error) {
			logger.error('Error updating user details:', error);
			toast.error('Error updating user details');
		} finally {
			setIsLoading(false);
		}
	};

	const handleSubmit = () => {
		if (isFormValid()) {
			updateUserData();
			logger.log('Primary Contact Number:', contactNumber);
			logger.log('Password:', password);
		} else {
			toast.error('Please correct the errors in the form.');
			logger.log('Form has validation errors. Please correct them.');
		}
	};

	const resetForm = () => {
		setContactNumber(initialContactNumber);
		setPassword('');
		setConfirmPassword('');
		setErrors({ password: '', confirmPassword: '' });
		toast.success('Data has been reset!');
	};

	const [password, setPassword] = useState('');
	const [confirmPassword, setConfirmPassword] = useState('');
	const [errors, setErrors] = useState({ password: '', confirmPassword: '' });

	const validatePassword = (passwordValue, confirmPasswordValue) => {
		let passwordError = '';
		let confirmPasswordError = '';

		// Example validation logic
		if (passwordValue.length < 6) {
			passwordError = 'Password must be at least 6 characters.';
		}
		if (passwordValue && !confirmPasswordValue) {
			confirmPasswordError = 'Passwords do not match.';
		} else if (confirmPasswordValue && passwordValue !== confirmPasswordValue) {
			confirmPasswordError = 'Passwords do not match.';
		}

		setErrors({ password: passwordError, confirmPassword: confirmPasswordError });
	};

	const handlePasswordChange = (value) => {
		setPassword(value);
		validatePassword(value, confirmPassword);
	};

	const handleConfirmPasswordChange = (value) => {
		setConfirmPassword(value);
		validatePassword(password, value);
	};

	return (
		<div className="sign-in-and-security-container">
			{isLoading ? (
				<div
					className="loader-container"
					style={{
						height: 'calc(100% - 60px)',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
					}}
				>
					<div className="loader"></div>
					<p>Loading...</p>
				</div>
			) : (
				<>
					<div className="sign-in-and-security-row1">
						<div className="sign-in-and-security-contact-number">
							<Label
								label="Primary Contact Number"
								fontSize={'14px'}
								fontWeight={'400'}
								className={'display-flex'}
							/>
							<div>
								<div className="phone-input-container">
									<PhoneInput
										value={contactNumber}
										onChange={handleChange}
										onBlur={handleBlur}
									/>
								</div>
								{phoneError && (
									<div
										style={{
											display: 'flex',
											justifyContent: 'start',
											marginTop: '2px',
											textAlign: 'left',
											width: '13rem',
										}}
									>
										<span style={{ color: 'red', fontSize: '14px' }}>
											{phoneError}
										</span>
									</div>
								)}
							</div>
						</div>
						<div className="sign-in-and-security-email">
							<Label
								label="Email Address"
								fontSize={'14px'}
								fontWeight={'400'}
								className={'display-flex'}
							/>
							<TextField value={emailId} isDisabled={true} notEditable={true} />
						</div>
					</div>
					<div className="sign-in-and-security-row2">
						<div className="sign-in-and-security-password">
							<Label
								label="New Password"
								fontSize={'14px'}
								fontWeight={'400'}
								className={'display-flex'}
							/>
							<PasswordInput
								label="Password"
								value={password}
								onChange={handlePasswordChange}
								error={errors.password}
							/>
						</div>
						<div className="sign-in-and-security-password">
							<Label
								label="Retype New Password"
								fontSize={'14px'}
								fontWeight={'400'}
								className={'display-flex'}
							/>
							<PasswordInput
								label="Confirm Password"
								value={confirmPassword}
								onChange={handleConfirmPasswordChange}
								error={errors.confirmPassword}
							/>
						</div>
					</div>
					<div className="sign-in-and-security-row3">
						<Button label="Reset" onClick={resetForm} className="cancel-button" />
						<Button label="Update" onClick={handleSubmit} className="update-button" />
					</div>
					<ToastContainer />
				</>
			)}
		</div>
	);
};

export default SignInAndSecurity;
